import React, { useState, useEffect, useCallback } from 'react';
import { useI18n } from '../i18n';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function SuggestionBox({ isAdmin = false }) {
  const { t } = useI18n();
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // Admin state
  const [suggestions, setSuggestions] = useState([]);
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminError, setAdminError] = useState('');

  const loadSuggestions = useCallback(async () => {
    setAdminLoading(true);
    setAdminError('');
    try {
      const res = await fetch(`${API_URL}/api/suggestions/admin`, {
        headers: { 'X-Admin-Token': 'evplanner-admin-2024' },
      });
      const data = await res.json();
      if (data.success) {
        setSuggestions(data.data || []);
      } else {
        setAdminError(data.message || t('suggestions.adminLoadError'));
      }
    } catch (err) {
      setAdminError(t('suggestions.adminLoadError'));
    } finally {
      setAdminLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (isAdmin) loadSuggestions();
  }, [isAdmin, loadSuggestions]);

  const handleConsider = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/suggestions/admin/${id}/consider`, {
        method: 'PATCH',
        headers: { 'X-Admin-Token': 'evplanner-admin-2024' },
      });
      const data = await res.json();
      if (data.success) {
        setSuggestions(prev => prev.map(s => s.id === id ? { ...s, status: 'CONSIDERED' } : s));
      }
    } catch { /* ignore */ }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta sugerencia?')) return;
    try {
      const res = await fetch(`${API_URL}/api/suggestions/admin/${id}`, {
        method: 'DELETE',
        headers: { 'X-Admin-Token': 'evplanner-admin-2024' },
      });
      const data = await res.json();
      if (data.success) {
        setSuggestions(prev => prev.filter(s => s.id !== id));
      }
    } catch { /* ignore */ }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/suggestions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: text.trim() }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || t('suggestions.errorSubmit'));
      setSuccess(true);
      setText('');
      setTimeout(() => setSuccess(false), 4000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Admin view: show list of suggestions
  if (isAdmin) {
    return (
      <div className="suggestion-box-panel">
        <h3>👑 {t('suggestions.adminList')}</h3>
        {adminLoading && <p>{t('suggestions.adminLoading')}</p>}
        {adminError && <div className="suggestion-error">⚠️ {adminError}</div>}
        {!adminLoading && suggestions.length === 0 && (
          <p style={{ color: '#666', fontStyle: 'italic' }}>{t('suggestions.noSuggestions')}</p>
        )}
        {suggestions.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {suggestions.map(s => (
              <div key={s.id} style={{
                padding: '12px', borderRadius: '8px', border: '1px solid #e5e7eb',
                background: s.status === 'CONSIDERED' ? '#d1fae5' : '#fff',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px' }}>
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: '0 0 6px 0', fontSize: '0.95em' }}>{s.text}</p>
                    <div style={{ fontSize: '0.78em', color: '#666' }}>
                      <span>{new Date(s.createdAt).toLocaleString('es-ES')}</span>
                      <span style={{
                        marginLeft: '8px', padding: '2px 6px', borderRadius: '4px', fontSize: '0.85em', fontWeight: 600,
                        background: s.status === 'CONSIDERED' ? '#d1fae5' : '#fef3c7',
                        color: s.status === 'CONSIDERED' ? '#065f46' : '#92400e',
                      }}>
                        {s.status === 'CONSIDERED' ? t('suggestions.considered') : t('suggestions.pending')}
                      </span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                    {s.status !== 'CONSIDERED' && (
                      <button
                        onClick={() => handleConsider(s.id)}
                        style={{ padding: '4px 8px', fontSize: '0.8em', borderRadius: '4px', border: '1px solid #059669', background: '#d1fae5', color: '#065f46', cursor: 'pointer' }}
                        title={t('suggestions.markConsidered')}
                      >✓</button>
                    )}
                    <button
                      onClick={() => handleDelete(s.id)}
                      style={{ padding: '4px 8px', fontSize: '0.8em', borderRadius: '4px', border: '1px solid #dc2626', background: '#fee2e2', color: '#991b1b', cursor: 'pointer' }}
                      title={t('suggestions.delete')}
                    >🗑️</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        <button
          type="button"
          onClick={loadSuggestions}
          style={{ marginTop: '12px', padding: '6px 14px', fontSize: '0.85em', borderRadius: '6px', border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer' }}
        >🔄 Recargar</button>
      </div>
    );
  }

  // Normal user view: submit suggestion
  return (
    <div className="suggestion-box-panel">
      <div className="suggestion-form-section">
        <h3>💡 {t('suggestions.title')}</h3>
        <p className="suggestion-desc">{t('suggestions.description')}</p>
        <form onSubmit={handleSubmit}>
          <textarea
            className="suggestion-textarea"
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder={t('suggestions.placeholder')}
            maxLength={2000}
            rows={4}
          />
          <div className="suggestion-form-footer">
            <span className="suggestion-char-count">{text.length}/2000</span>
            <button type="submit" disabled={submitting || !text.trim()} className="suggestion-submit-btn">
              {submitting ? t('suggestions.submitting') : t('suggestions.submit')}
            </button>
          </div>
        </form>
        {success && <div className="suggestion-success">✅ {t('suggestions.success')}</div>}
        {error && <div className="suggestion-error">⚠️ {error}</div>}
      </div>
    </div>
  );
}
